import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import { useUIStore } from '@/shared/stores/ui.store';
import { aiInsightsApi } from '@/features/ai/api';
import type {
  Deal,
  PipelineStat,
  DealsFilters,
  CreateDealPayload,
  UpdateDealPayload,
} from '../types';
import { cacheDealDetail, dealDetailQueryKey } from './dealCache';

export interface DealsResponse {
  deals: Deal[];
  pipelineStats: PipelineStat[];
}

// ─── Queries ────────────────────────────────────────────────────────────────

/**
 * The Deals pipeline, scoped by the Topbar branch switcher (BizPlus parity,
 * Phase 0.3) unless the caller already set an explicit filters.branchId — an
 * explicit choice always wins over the ambient one.
 */
export function useDeals(filters: DealsFilters = {}) {
  const activeBranchId = useUIStore((s) => s.activeBranchId);
  const scoped: DealsFilters = { branchId: activeBranchId, ...filters };
  return useQuery({
    queryKey: ['deals', 'list', scoped],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (scoped.stage) params.set('stage', scoped.stage);
      if (scoped.source) params.set('source', scoped.source);
      if (scoped.assignedTo) params.set('assignedTo', scoped.assignedTo);
      if (scoped.contact_id) params.set('contact_id', scoped.contact_id);
      if (scoped.branchId && scoped.branchId !== 'all') params.set('branchId', scoped.branchId);
      const qs = params.toString();
      const { data } = await api.get<DealsResponse>(`/api/deals${qs ? `?${qs}` : ''}`);
      return data;
    },
    placeholderData: keepPreviousData,
  });
}

export function useDeal(dealId: string | null) {
  return useQuery({
    queryKey: dealId ? dealDetailQueryKey(dealId) : ['deals', 'detail', dealId],
    queryFn: async () => {
      const { data } = await api.get<any>(`/api/deals/${dealId}`);
      return (data?.deal || data) as Deal;
    },
    enabled: !!dealId,
  });
}

/**
 * Deals the current user handed off to someone else (assigned_by === me). Kept
 * separate so they can be merged back into the caller's own list — after a
 * hand-off the deal's owner becomes the recipient, so it would otherwise vanish
 * from an employee's "assigned to me" list.
 */
export function useHandedOffDeals() {
  return useQuery({
    queryKey: ['deals', 'handed-off'],
    queryFn: async () => {
      const { data } = await api.get<{ deals: Deal[] }>('/api/handed-off-deals');
      return data.deals ?? [];
    },
    placeholderData: keepPreviousData,
  });
}

/**
 * AI win-probability scores keyed by dealId. The endpoint returns scores for
 * all open deals globally; callers only ever read scores for deals already in
 * their own visible list, so no cross-user data is surfaced.
 */
export function useDealScores(enabled = true) {
  return useQuery({
    queryKey: ['deals', 'ai-scores'],
    queryFn: async () => {
      const data = await aiInsightsApi.dealScores();
      const byId: Record<string, number> = {};
      (data.deals ?? []).forEach((deal) => {
        byId[deal.dealId] = deal.score;
      });
      return byId;
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

// ─── Mutations ───────────────────────────────────────────────────────────────

export function useDealMutations() {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['deals'] });

  /**
   * Close a deal won or lost.
   *
   * Deliberately NOT the generic deal PATCH. That route accepts a stage with no
   * outcome handling, so moving a deal to closed_lost through it records no
   * reason and the win/loss report under-counts silently. This route enforces
   * the reason, and clears a stale one when a deal is re-won or reopened.
   */
  const setDealOutcome = useMutation({
    mutationFn: async ({ id, stage, lostReasonId, lostReasonNote }: {
      id: string; stage: string; lostReasonId?: string; lostReasonNote?: string;
    }) => {
      const { data } = await api.post(`/api/deals-outcome/${id}/outcome`, {
        stage, lostReasonId, lostReasonNote,
      });
      return data;
    },
    onSuccess: invalidate,
  });

  const createDeal = useMutation({
    mutationFn: async (payload: CreateDealPayload) => {
      const { data } = await api.post<{ ok: boolean; id: string }>('/api/deals', payload);
      return data;
    },
    onSuccess: invalidate,
  });

  const updateDeal = useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: UpdateDealPayload }) => {
      const { data: result } = await api.patch<{ ok: boolean }>(`/api/deals/${id}`, payload);
      const { data } = await api.get<any>(`/api/deals/${id}`);
      const deal = (data?.deal || data) as Deal;
      return { ...result, deal: { ...deal, id: deal.id || (deal as any)._id || id } };
    },
    onSuccess: (data, variables) => {
      cacheDealDetail(queryClient, variables.id, data.deal);
      return invalidate();
    },
  });

  const deleteDeal = useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.delete<{ ok: boolean }>(`/api/deals/${id}`);
      return data;
    },
    onSuccess: invalidate,
  });

  // ── CRM 2.0 — deal hand-off (additive /api/deals/:id/assign + accept/reject) ──
  const assignDeal = useMutation({
    mutationFn: async ({ id, assignee, note }: { id: string; assignee: string; note?: string }) => {
      const { data } = await api.post<{ ok: boolean; assignment_status: string }>(
        `/api/deals/${id}/assign`, { assignee, note: note ?? '' },
      );
      return data;
    },
    onSuccess: invalidate,
  });

  const respondAssignment = useMutation({
    mutationFn: async ({ id, action }: { id: string; action: 'accept' | 'reject' }) => {
      const { data } = await api.post<{ ok: boolean; assignment_status: string }>(
        `/api/deals/${id}/assignment/${action}`,
      );
      return data;
    },
    onSuccess: invalidate,
  });

  return { createDeal, updateDeal, deleteDeal, assignDeal, respondAssignment, setDealOutcome };
}
